import { apiSlice } from './apiSlice';
import {
  AddBankAccountRequest,
  AddBankAccountResponse,
  AddCryptoWalletRequest,
  AddUPIAccountRequest,
  AddUPIAccountResponse,
  BankAccountsResponse,
  CryptoWallet,
  CryptoNetwork,
  CreateDepositWithdrawResponse,
  CryptoDepositRequest,
  CreateUpiTransactionResponse,
  DeleteAccountResponse,
  DepositAccountResponse,
  DepositWithdrawQueryParams,
  DepositWithdrawRequest,
  DepositWithdrawResponse,
  UpiAccountResponse,
  UploadFileResponse,
} from '@/types/Account';

export const accountApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    addBankAccount: builder.mutation<AddBankAccountResponse, AddBankAccountRequest>({
      query: (body) => ({
        url: '/bank',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    addUpiAccount: builder.mutation<AddUPIAccountResponse, AddUPIAccountRequest>({
      query: (body) => ({
        url: '/upi',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    depositWithdrawRequest: builder.mutation<
      CreateDepositWithdrawResponse | CreateUpiTransactionResponse,
      DepositWithdrawRequest
    >({
      query: ({ method, ...body }) => ({
        url: `/${method}/deposit-withdraw-request`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account', 'User'],
    }),

    cryptoDepositRequest: builder.mutation<CreateDepositWithdrawResponse, CryptoDepositRequest>({
      query: (body) => ({
        url: '/crypto/deposit-withdraw-request',
        method: 'POST',
        body: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...body },
      }),
      invalidatesTags: ['Account', 'User'],
    }),
    getCryptoWallets: builder.query<CryptoWallet[], void>({
      query: () => ({ url: '/crypto', method: 'GET' }),
      transformResponse: (response: CryptoWallet[] | { wallets?: CryptoWallet[] }) =>
        Array.isArray(response) ? response : (response.wallets ?? []),
      providesTags: ['Account'],
    }),
    getCryptoNetworks: builder.query<CryptoNetwork[], void>({
      query: () => ({ url: '/crypto/networks', method: 'GET' }),
      transformResponse: (response: CryptoNetwork[] | { data?: CryptoNetwork[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
    }),
    addCryptoWallet: builder.mutation<AddBankAccountResponse, AddCryptoWalletRequest>({
      query: (body) => ({ url: '/crypto', method: 'POST', body }),
      invalidatesTags: ['Account'],
    }),
    deleteCryptoWallet: builder.mutation<DeleteAccountResponse, string>({
      query: (id) => ({ url: `/crypto/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Account'],
    }),

    deleteAccount: builder.mutation<DeleteAccountResponse, { id: string; type: 'upi' | 'bank' }>({
      query: ({ id, type }) => ({
        url: `/${type}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Account'],
    }),
    getBankAccount: builder.query<BankAccountsResponse, void>({
      query: () => ({
        url: '/bank',
        method: 'GET',
      }),
      providesTags: ['Account'],
    }),
    getUpiAccount: builder.query<UpiAccountResponse, void>({
      query: () => ({
        url: '/upi',
        method: 'GET',
      }),
      providesTags: ['Account'],
    }),
    getDepositWithdraw: builder.query<DepositWithdrawResponse, DepositWithdrawQueryParams>({
      query: (params) => ({
        url: '/banker/my-deposit-withdraw',
        method: 'GET',
        params,
      }),
      providesTags: ['Account'],
    }),
    uploadFile: builder.mutation<UploadFileResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/upload',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Account'],
    }),
    getDepositAccounts: builder.query<DepositAccountResponse, void>({
      query: () => ({
        url: '/banker/show-account',
        method: 'GET',
      }),
      providesTags: ['Account'],
    }),
  }),
});

export const {
  useAddBankAccountMutation,
  useAddUpiAccountMutation,
  useGetBankAccountQuery,
  useDeleteAccountMutation,
  useDepositWithdrawRequestMutation,

  useCryptoDepositRequestMutation,
  useGetCryptoWalletsQuery,
  useGetCryptoNetworksQuery,
  useAddCryptoWalletMutation,
  useDeleteCryptoWalletMutation,

  useGetDepositWithdrawQuery,
  useUploadFileMutation,
  useGetUpiAccountQuery,
  useGetDepositAccountsQuery,
} = accountApi;
